/**
 * API Client with automatic token refresh
 *
 * Provides a fetch wrapper that automatically handles:
 * - Adding authorization headers
 * - Refreshing tokens on 401 responses
 * - Redirecting to login on auth failures
 */

interface TokenStorage {
  getAccessToken: () => string | null;
  getRefreshToken: () => string | null;
  setTokens: (access: string, refresh: string) => void;
  clearTokens: () => void;
}

/** Default token storage using localStorage */
const defaultTokenStorage: TokenStorage = {
  getAccessToken: () => localStorage.getItem('accessToken'),
  getRefreshToken: () => localStorage.getItem('refreshToken'),
  setTokens: (access, refresh) => {
    localStorage.setItem('accessToken', access);
    localStorage.setItem('refreshToken', refresh);
  },
  clearTokens: () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  },
};

// ── Single-flight token refresh ────────────────────────────────────────
//
// Bug #11 (Library) / Users #6 — intermittent 401 bursts. Previously the
// three request entry points (request / requestRaw / requestStream) each
// flipped a shared boolean `isRefreshing` and managed their own
// subscriber queue. Under concurrent load (e.g. a rail mount firing 5
// parallel GETs right as the access token expires) each request could
// observe `isRefreshing === false`, start its OWN refresh, and — because
// refresh-token rotation invalidates the prior refresh token — all but
// the first refresh would fail, clearing tokens and bouncing the user to
// /login.
//
// The fix is a single shared in-flight refresh PROMISE. Whoever observes
// a 401 first starts the refresh; every other caller awaits the SAME
// promise instead of starting a competing one. Refresh-token rotation
// therefore happens exactly once per expiry window.
let refreshPromise: Promise<string | null> | null = null;

/**
 * Single-flight wrapper around `refreshAccessToken`. If a refresh is
 * already in progress, the in-flight promise is returned so concurrent
 * callers coalesce onto one network round-trip (and one refresh-token
 * rotation). The promise is cleared once it settles so the next expiry
 * window starts a fresh refresh.
 */
function triggerRefresh(storage: TokenStorage): Promise<string | null> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = refreshAccessToken(storage).finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

/**
 * Decode the access token's `exp` (seconds since epoch). Returns null
 * when there's no token or it can't be parsed.
 */
function getTokenExpiry(storage: TokenStorage): number | null {
  const access = storage.getAccessToken();
  if (!access) return null;
  try {
    const payload = JSON.parse(atob(access.split('.')[1]));
    return typeof payload?.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

/**
 * Proactive pre-expiry refresh. Called at the top of every request: if
 * the current access token expires within `PROACTIVE_REFRESH_SKEW_MS`,
 * refresh it BEFORE issuing the request (single-flight, so a burst of
 * concurrent requests triggers one refresh and all reuse the result).
 * This collapses the window in which a 401 can fire at all.
 *
 * Never throws — on any failure it returns the current token (possibly
 * stale) and lets the normal 401-retry path handle it.
 */
const PROACTIVE_REFRESH_SKEW_MS = 30_000;
async function ensureFreshToken(storage: TokenStorage): Promise<string | null> {
  const current = storage.getAccessToken();
  if (!current || !storage.getRefreshToken()) return current;
  const exp = getTokenExpiry(storage);
  if (exp == null) return current;
  const msUntilExpiry = exp * 1000 - Date.now();
  if (msUntilExpiry > PROACTIVE_REFRESH_SKEW_MS) return current;
  try {
    const refreshed = await triggerRefresh(storage);
    return refreshed ?? current;
  } catch {
    return current;
  }
}

/**
 * Detect token audience from the access token's JWT payload. Drives
 * the choice of refresh endpoint — tenant tokens go to /auth/refresh,
 * sysadmin tokens go to /auth/login/refresh-sysadmin.
 *
 * Returns null when there's no access token or the payload can't be
 * decoded (caller should treat as a logout).
 */
function detectTokenAudience(storage: TokenStorage): 'argusai' | 'argusai-admin' | null {
  const access = storage.getAccessToken();
  if (!access) return null;
  try {
    const payload = JSON.parse(atob(access.split('.')[1]));
    return payload?.aud === 'argusai-admin' ? 'argusai-admin' : 'argusai';
  } catch {
    return null;
  }
}

/**
 * Attempts to refresh the access token. Audience-aware: sysadmin
 * tokens use /auth/login/refresh-sysadmin, tenant tokens use
 * /auth/refresh. Without this distinction sysadmin sessions hit a
 * cascade of 401s on the tenant endpoint and end up bounced to /login
 * (UI bug #1 caught by the Playwright harness).
 */
async function refreshAccessToken(
  storage: TokenStorage
): Promise<string | null> {
  const refreshToken = storage.getRefreshToken();
  if (!refreshToken) {
    return null;
  }

  const audience = detectTokenAudience(storage);
  const endpoint =
    audience === 'argusai-admin'
      ? '/api/v1/auth/login/refresh-sysadmin'
      : '/api/v1/auth/refresh';

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    storage.setTokens(data.accessToken, data.refreshToken);
    return data.accessToken;
  } catch {
    return null;
  }
}

/** Shape of the API's standard error response.
 *
 * Matches `apiErrorResponseSchema` in @argusai/shared. Duplicated here
 * (rather than imported) because @argusai/shared currently pulls in
 * server-only deps (pino, drizzle) and we don't want to bloat the SPA
 * bundle with those. The handful of fields we touch are stable.
 */
interface ApiErrorBody {
  success?: false;
  error?: {
    code?: string;
    message?: string;
    details?: {
      fieldErrors?: Array<{ path: string; message: string; code?: string }>;
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
}

/**
 * The one 401 that a REFRESH CANNOT FIX.
 *
 * Foundations 2 (Users rail) / B2a-T3 review BF-03. The API refuses every
 * request from a person who is no longer live in the organization their token
 * names — locked, deactivated, soft-deleted, membership lapsed or removed —
 * with 401 `PRINCIPAL_NOT_LIVE` (`@argusai/shared` `ErrorCode`, duplicated
 * here for the same reason `ApiErrorBody` is: the SPA does not pull pino and
 * drizzle in to read one string).
 *
 * Every 401 handler below used to treat a 401 as "the access token expired":
 * refresh, retry, and only give up if the refresh FAILED. For this code the
 * refresh succeeds — it is a different question, answered from a different
 * row — so the retry 401s again, `newToken` is truthy, and the branch that
 * clears the session is never reached. The app sits there looking signed in,
 * sidebar and avatar and all, over a dead-end error whose Retry can only 401
 * again, while each attempt churns a refresh-token DELETE + INSERT. That is
 * verbatim the failure the expired-token comment below says was fixed.
 *
 * So this code SHORT-CIRCUITS the refresh: the session is over, end it.
 */
const PRINCIPAL_NOT_LIVE = 'PRINCIPAL_NOT_LIVE';

/**
 * Read the error code off a 401 without consuming the caller's response body.
 * Never throws: an unreadable or non-JSON body is simply not this code.
 */
async function isPrincipalNotLive(response: Response): Promise<boolean> {
  try {
    const body = (await response.clone().json()) as ApiErrorBody;
    return body?.error?.code === PRINCIPAL_NOT_LIVE;
  } catch {
    return false;
  }
}

/** Build a human-readable error message from the server's error body.
 *
 * For VALIDATION_ERROR responses carrying `details.fieldErrors`, expand
 * the generic "Validation failed" into something the user can act on:
 *
 *   - 1 field error  → "name: Required"
 *   - 2-3 field errs → "name: Required; framework: Must be one of …"
 *   - 4+ field errs  → "Validation failed (4 issues): name: Required; …"
 *
 * Bug H4 (UI sweep 2026-05-11): the SPA previously just displayed
 * `errorData.error.message` which was always "Validation failed" with
 * no field detail — the founder hit this on every sysadmin catalog
 * create button. Enriching here means every existing caller (which
 * just renders `err.message` in a toast) gets useful feedback without
 * having to change each call site.
 */
export function formatErrorMessage(
  errorData: ApiErrorBody | undefined,
  status: number,
): string {
  const message = errorData?.error?.message;
  const code = errorData?.error?.code;
  const fieldErrors = errorData?.error?.details?.fieldErrors;

  if (
    code === 'VALIDATION_ERROR' &&
    Array.isArray(fieldErrors) &&
    fieldErrors.length > 0
  ) {
    const fmt = (fe: { path: string; message: string }) =>
      fe.path ? `${fe.path}: ${fe.message}` : fe.message;

    if (fieldErrors.length === 1) {
      return fmt(fieldErrors[0]);
    }
    if (fieldErrors.length <= 3) {
      return fieldErrors.map(fmt).join('; ');
    }
    const head = fieldErrors.slice(0, 3).map(fmt).join('; ');
    return `${message ?? 'Validation failed'} (${fieldErrors.length} issues): ${head}; …`;
  }

  return message || `Request failed: ${status}`;
}

// Lazy import to keep the api-client module free of cycles. The tracker
// is intentionally a tiny pure module; we resolve it eagerly here but
// don't reach into React state.
import { recordApiCall, recordFailedApiCall } from './failed-api-tracker';

interface ApiClientOptions {
  baseUrl?: string;
  tokenStorage?: TokenStorage;
  onAuthError?: () => void;
}

/**
 * Creates an API client with automatic token refresh
 */
export function createApiClient(options: ApiClientOptions = {}) {
  const {
    baseUrl = '/api/v1',
    tokenStorage = defaultTokenStorage,
    onAuthError = () => {
      window.location.href = '/login';
    },
  } = options;

  /**
   * Makes an authenticated API request with automatic token refresh
   */
  async function request<T = unknown>(
    endpoint: string,
    init: RequestInit = {}
  ): Promise<T> {
    // Bug #11 — proactively refresh a near-expiry token (single-flight)
    // before issuing the request so concurrent calls don't each race
    // into a 401.
    const accessToken = await ensureFreshToken(tokenStorage);

    // Add authorization header if we have a token
    const headers = new Headers(init.headers);
    if (accessToken) {
      headers.set('Authorization', `Bearer ${accessToken}`);
    }
    if (!headers.has('Content-Type') && init.body) {
      headers.set('Content-Type', 'application/json');
    }

    const url = endpoint.startsWith('/') ? `${baseUrl}${endpoint}` : endpoint;

    let response = await fetch(url, {
      ...init,
      headers,
    });

    // If we get a 401 and have a refresh token, refresh once (shared,
    // single-flight) and retry. Concurrent 401s coalesce onto the same
    // refresh promise via triggerRefresh(), so refresh-token rotation
    // happens exactly once per expiry window.
    if (response.status === 401) {
      // Only a caller that HELD an access token believed it was signed in.
      // An anonymous 401 (e.g. /branding on the login page) is expected and
      // must fall through untouched — redirecting there would bounce the login
      // page to itself.
      const wasSignedIn = !!tokenStorage.getAccessToken();
      let newToken: string | null = null;

      // BF-03 — PRINCIPAL_NOT_LIVE is terminal. Refreshing it succeeds and
      // changes nothing, so do not refresh: clear and go to login.
      const notLive = wasSignedIn && (await isPrincipalNotLive(response));

      if (!notLive && tokenStorage.getRefreshToken()) {
        try {
          newToken = await triggerRefresh(tokenStorage);
        } catch {
          newToken = null;
        }
      }

      if (newToken) {
        headers.set('Authorization', `Bearer ${newToken}`);
        response = await fetch(url, { ...init, headers });
      } else if (wasSignedIn) {
        // Refresh failed, OR there was no refresh token to try. Both mean the
        // session is dead, so end it the same way: clear and send the user to
        // login. Previously the no-refresh-token case skipped this block
        // entirely and the 401 fell through to the generic error renderer,
        // leaving the app looking signed in — sidebar, avatar and all — over a
        // dead-end "Could not load conversation history (Access token has
        // expired)" whose Retry button could only ever 401 again.
        //
        // Only the caller that drove the failed refresh (or any caller seeing
        // a null result) trips this; the shared promise means we don't clear
        // tokens N times for N concurrent 401s.
        tokenStorage.clearTokens();
        onAuthError();
        throw new Error('Session expired');
      }
    }

    // Bug-report tracker: every observed call (success or failure)
    // feeds the recent-history ring buffer; failures additionally feed
    // the "what just broke" buffer. bBUG3: on non-2xx we ALSO capture
    // the response body so triagers reading the GitHub issue can see
    // the actual error message (not just method/path/status). The body
    // is sanitized + truncated inside the tracker — see
    // failed-api-tracker.ts.
    //
    // We read the body via `response.clone().text()` so the original
    // response stream is still consumable for the error-envelope JSON
    // parse below. clone() shares the underlying body reader so this
    // is cheap. Wrapped in try/catch — telemetry never throws.
    let trackerBody: string | undefined;
    if (response.status >= 400) {
      try {
        trackerBody = await response.clone().text();
      } catch {
        /* body unreadable — drop and continue */
      }
    }
    try {
      recordApiCall({
        method: (init.method ?? 'GET').toString(),
        url,
        status: response.status,
        responseBody: trackerBody,
      });
    } catch { /* tracker must never throw */ }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(
        formatErrorMessage(errorData, response.status)
      );
      (error as any).status = response.status;
      (error as any).data = errorData;
      // Surface fieldErrors directly on the thrown Error so callers that
      // want to highlight inline form fields can read them without
      // reaching into `.data.error.details.fieldErrors`.
      const fieldErrors = errorData?.error?.details?.fieldErrors;
      if (Array.isArray(fieldErrors)) {
        (error as any).fieldErrors = fieldErrors;
      }
      throw error;
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return null as T;
    }

    return response.json();
  }

  /**
   * Makes an authenticated request returning the raw Response (no JSON
   * parsing). Used for binary downloads — e.g. Phase 5 PR-F audit
   * export bundles where the body is a gzipped tarball.
   *
   * Shares the auth + refresh logic with `request<T>` but returns the
   * Response so the caller can call `.blob()` or `.arrayBuffer()`.
   *
   * NOTE: a 4xx/5xx response is NOT thrown — the caller inspects
   * `response.ok` so the error body can be read off the same Response.
   */
  async function requestRaw(
    endpoint: string,
    init: RequestInit = {}
  ): Promise<Response> {
    // Bug #11 — proactive single-flight refresh, then attach the header.
    const accessToken = await ensureFreshToken(tokenStorage);
    const headers = new Headers(init.headers);
    if (accessToken) {
      headers.set('Authorization', `Bearer ${accessToken}`);
    }
    const url = endpoint.startsWith('/') ? `${baseUrl}${endpoint}` : endpoint;

    let response = await fetch(url, { ...init, headers });

    // Bug #11 — on 401, wait on the SHARED refresh promise (coalesced
    // with any concurrent request/requestStream refresh) and retry once,
    // instead of each entry point starting its own competing refresh.
    if (response.status === 401 && tokenStorage.getRefreshToken()) {
      // BF-03 — a refresh cannot revive a person who is no longer live; it
      // just mints another token the next request refuses. End the session.
      const notLive = await isPrincipalNotLive(response);
      let newToken: string | null = null;
      if (!notLive) {
        try {
          newToken = await triggerRefresh(tokenStorage);
        } catch {
          newToken = null;
        }
      }
      if (newToken) {
        headers.set('Authorization', `Bearer ${newToken}`);
        response = await fetch(url, { ...init, headers });
      } else {
        tokenStorage.clearTokens();
        onAuthError();
      }
    }

    // Bug-report tracker: every observed call (success or failure).
    // bBUG3 — capture failure body for the binary-download path too;
    // the caller may want to surface the error to the user but the
    // tracker is what feeds the GitHub issue.
    let trackerBody: string | undefined;
    if (response.status >= 400) {
      try {
        trackerBody = await response.clone().text();
      } catch {
        /* body unreadable — drop and continue */
      }
    }
    try {
      recordApiCall({
        method: (init.method ?? 'GET').toString(),
        url,
        status: response.status,
        responseBody: trackerBody,
      });
    } catch { /* tracker must never throw */ }

    return response;
  }

  /**
   * Makes an authenticated streaming request (Server-Sent Events).
   *
   * Like `requestRaw`, this returns the raw `Response` (the caller is
   * responsible for reading `response.body`) — but with the SAME
   * 401-refresh-and-retry logic as `request<T>`. Used by the SSE
   * call sites (Argus Live chat panel, Ask Argus, helper-chats) which
   * previously each duplicated a raw `fetch()` and manually pulled
   * `accessToken` out of `localStorage`, bypassing the refresh logic.
   *
   * The body is provided as `bodyJson` (not stringified) for the
   * common JSON POST case. Pass `null` for a no-body GET. The
   * `Content-Type: application/json` header is added automatically
   * when bodyJson is non-null.
   *
   * The caller's `AbortSignal` is honored on both the initial fetch
   * AND the retry-after-refresh fetch.
   *
   * The returned response may carry a non-ok status (e.g. 403 if the
   * user lacks the permission); the caller inspects `response.ok` and
   * reads the body off the same Response, matching `requestRaw`.
   *
   * Bug S3 P1 #7: the Argus Live chat panel previously called raw
   * `fetch('/api/v1/ecosystem/query', ...)` with a hand-attached
   * `Bearer ${localStorage.accessToken}`. On long-lived editor
   * sessions the access token expired and chat silently broke — the
   * user just saw "Chat failed: HTTP 401". `requestStream` plugs
   * the SSE path into the same refresh-then-retry loop as every
   * other JSON endpoint.
   */
  async function requestStream(
    endpoint: string,
    init: {
      method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
      bodyJson?: unknown;
      headers?: Record<string, string>;
      signal?: AbortSignal;
      credentials?: RequestCredentials;
    } = {}
  ): Promise<Response> {
    const method = init.method ?? (init.bodyJson === undefined ? 'GET' : 'POST');
    const headers: Record<string, string> = {
      Accept: 'text/event-stream',
      ...(init.headers ?? {}),
    };
    let bodyString: string | undefined;
    if (init.bodyJson !== undefined && init.bodyJson !== null) {
      bodyString = JSON.stringify(init.bodyJson);
      if (!headers['Content-Type']) headers['Content-Type'] = 'application/json';
    }

    // Bug #11 — proactive single-flight refresh before the stream opens.
    const accessToken = await ensureFreshToken(tokenStorage);
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
    const url = endpoint.startsWith('/') ? `${baseUrl}${endpoint}` : endpoint;

    // Each call gets a SHALLOW CLONE of the headers map so tests + caller
    // code can capture an immutable snapshot from the first fetch even
    // after we mutate `headers['Authorization']` for the retry. The
    // `request<T>` path doesn't have this property — but the
    // refresh-then-retry there constructs a fresh Headers instance via
    // `new Headers(init.headers)`, which has the same effect.
    const buildInit = (): RequestInit => ({
      method,
      headers: { ...headers },
      body: bodyString,
      signal: init.signal,
      credentials: init.credentials ?? 'include',
    });

    let response = await fetch(url, buildInit());

    // Bug #11 — on 401, coalesce onto the SHARED single-flight refresh
    // promise and retry once (matches request / requestRaw).
    if (response.status === 401 && tokenStorage.getRefreshToken()) {
      // BF-03 — PRINCIPAL_NOT_LIVE is terminal here too. Cloning a 401 body is
      // safe on this path: the SSE stream never started, the body is the small
      // JSON error envelope, and the original `response` is discarded either
      // way (retried, or thrown past).
      const notLive = await isPrincipalNotLive(response);
      let newToken: string | null = null;
      if (!notLive) {
        try {
          newToken = await triggerRefresh(tokenStorage);
        } catch {
          newToken = null;
        }
      }
      if (newToken) {
        headers['Authorization'] = `Bearer ${newToken}`;
        response = await fetch(url, buildInit());
      } else {
        tokenStorage.clearTokens();
        onAuthError();
      }
    }

    // Bug-report tracker: every observed call (success or failure).
    // bBUG3 — SSE streams cannot have their body cloned-and-read
    // without breaking the stream (response.body is consumed once),
    // so this path INTENTIONALLY does NOT pass responseBody. Failure
    // bodies on SSE endpoints (e.g. a 503 before the stream starts)
    // still show up in method/path/status — engineers can correlate
    // with server logs from there.
    try {
      recordApiCall({
        method,
        url,
        status: response.status,
      });
    } catch { /* tracker must never throw */ }

    return response;
  }

  // ── Chat-stream token helpers (founder-reported 2026-09-03) ────────────
  //
  // `useChatStream` owns its own fetch (it must read the SSE body, which
  // `request<T>` cannot hand back), so it never went through the proactive
  // near-expiry refresh or the 401-refresh-retry above. After an idle
  // stretch the access token lapsed and the very next question rendered an
  // HTTP_401 card even though a perfectly good refresh token was sitting in
  // storage. These two helpers expose exactly the pieces the stream needs
  // — nothing else changes about how tokens are stored or rotated.

  /**
   * Proactive near-expiry check + single-flight refresh, then the current
   * access token (possibly the freshly rotated one). Never throws.
   */
  async function ensureFreshAccessToken(): Promise<string | null> {
    return ensureFreshToken(tokenStorage);
  }

  /**
   * The stream saw a 401. Run ONE single-flight refresh (coalesced with any
   * concurrent request/requestRaw/requestStream refresh) and return the new
   * token; when there is no refresh token or the refresh fails, the session
   * is dead — clear it and fire `onAuthError`, returning null so the caller
   * renders its error state instead of retrying forever.
   */
  async function handleStreamUnauthorized(): Promise<string | null> {
    if (tokenStorage.getRefreshToken()) {
      let newToken: string | null = null;
      try {
        newToken = await triggerRefresh(tokenStorage);
      } catch {
        newToken = null;
      }
      if (newToken) return newToken;
    }
    tokenStorage.clearTokens();
    onAuthError();
    return null;
  }

  return {
    get: <T = unknown>(endpoint: string, init?: RequestInit) =>
      request<T>(endpoint, { ...init, method: 'GET' }),

    post: <T = unknown>(endpoint: string, body?: unknown, init?: RequestInit) =>
      request<T>(endpoint, {
        ...init,
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined,
      }),

    put: <T = unknown>(endpoint: string, body?: unknown, init?: RequestInit) =>
      request<T>(endpoint, {
        ...init,
        method: 'PUT',
        body: body ? JSON.stringify(body) : undefined,
      }),

    patch: <T = unknown>(endpoint: string, body?: unknown, init?: RequestInit) =>
      request<T>(endpoint, {
        ...init,
        method: 'PATCH',
        body: body ? JSON.stringify(body) : undefined,
      }),

    delete: <T = unknown>(endpoint: string, init?: RequestInit) =>
      request<T>(endpoint, { ...init, method: 'DELETE' }),

    request,
    requestRaw,
    requestStream,
    ensureFreshAccessToken,
    handleStreamUnauthorized,
  };
}

/** Default API client instance */
export const apiClient = createApiClient();

/**
 * Module-level bindings of the chat-stream token helpers to the DEFAULT
 * client (same localStorage-backed token storage the Ask Argus route reads
 * its bearer from). `useChatStream` imports these so a stream refreshes
 * tokens exactly like every other request. Defined as functions (not
 * destructured constants) so a test can `vi.mock` this module and control
 * both paths.
 */
export function ensureFreshAccessToken(): Promise<string | null> {
  return apiClient.ensureFreshAccessToken();
}

export function handleStreamUnauthorized(): Promise<string | null> {
  return apiClient.handleStreamUnauthorized();
}

// Re-export for convenience
export { defaultTokenStorage };
